import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { mfaResendEmailCode, mfaVerifyEmailCode } from 'config/axiosInstance';
import CodeField from './CodeField';
import { ErrorBox, MfaFrame, MfaStepProps, SubmitButton, useAttempt, useStepFailure } from './MfaFrame';
import { classifyMfaError } from './mfaErrors';
import { useCountdown } from './useCountdown';

/**
 * 送り直しの間隔と、1つのチャレンジで送れる数（最初の1通を含む）。サーバーの
 * MFA_EMAIL_RESEND_INTERVAL（60秒）・MFA_EMAIL_MAX_SENDS（5通）に合わせる
 * （services/backend/yometel_alcohol_check_backend/settings.py）。
 * 応答の本文には残りの数が無いので、この画面で数える。サーバーの判定（429 の Retry-After・
 * mfa_email_resend_limit）が来たら、そちらに合わせる。
 */
export const RESEND_INTERVAL_SECONDS = 60;
export const MAX_EMAIL_SENDS = 5;

/** メールで届いた6桁のコード（mfa が email_code_required）。 */
const EmailCodeStep: React.FC<MfaStepProps> = ({ challenge, email, onNext, onRestart }) => {
  const { t } = useTranslation('login');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  // 画面を開いた時点で1通送ってある（ログインの 202）。
  const [resendsLeft, setResendsLeft] = useState(MAX_EMAIL_SENDS - 1);
  const [resendWait, startResendWait] = useCountdown(RESEND_INTERVAL_SECONDS);
  const [resendNote, setResendNote] = useState('');
  const [token, setToken] = useState(challenge.token);
  const failure = useStepFailure(onRestart);
  // 照合と送り直しは別の試行（片方を始めても、もう片方を abort しない）。どちらも送信中は戻れない。
  const beginVerify = useAttempt();
  const beginResend = useAttempt();

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      failure.setText(t('mfa.error-code-format'));
      return;
    }
    setBusy(true);
    failure.setText('');
    const signal = beginVerify();
    try {
      const next = await mfaVerifyEmailCode(token, code, { signal });
      if (!signal.aborted) onNext(next);
    } catch (error) {
      if (signal.aborted) return;
      failure.handle(error);
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setResendNote('');
    failure.setText('');
    const signal = beginResend();
    try {
      const next = await mfaResendEmailCode(token, { signal });
      if (signal.aborted) return;
      // 送り直しでは mfa_token は替わらないが、返った値を使う。
      setToken(next.token);
      setResendsLeft((left) => Math.max(0, left - 1));
      startResendWait(RESEND_INTERVAL_SECONDS);
      setCode('');
      setResendNote(t('mfa.resend-done'));
    } catch (error) {
      if (signal.aborted) return;
      const failed = classifyMfaError(error);
      if (failed.kind === 'resend_too_soon') {
        startResendWait(failed.seconds ?? RESEND_INTERVAL_SECONDS);
      } else if (failed.kind === 'resend_limit') {
        setResendsLeft(0);
        setResendNote(t('mfa.resend-none'));
      } else {
        failure.handle(error);
      }
    } finally {
      if (!signal.aborted) setResending(false);
    }
  };

  const errorId = failure.text ? 'mfa-email-error' : undefined;
  const canResend = resendsLeft > 0 && resendWait === 0 && !resending && !busy;
  return (
    <MfaFrame
      title={t('mfa.email-title')}
      subtitle={t('mfa.email-subtitle', { email })}
      onBack={() => onRestart(null)}
      backDisabled={busy || resending}
    >
      <form onSubmit={submit} className="flex flex-col gap-6" noValidate>
        <CodeField id="mfa-email-code" value={code} errorId={errorId} onChange={(value) => {
          setCode(value);
          failure.setText('');
        }} />
        <ErrorBox id="mfa-email-error" text={failure.text} />
        <SubmitButton busy={busy} disabled={failure.waiting || resending} label={t('mfa.email-button')} />
      </form>
      <div className="flex flex-col items-center gap-2">
        <button
          type="button"
          className={`px-4 py-2 rounded-md border ${
            canResend ? 'border-primary/300 text-primary/300' : 'border-neutral/50 text-neutral/300 cursor-not-allowed'
          }`}
          onClick={resend}
          disabled={!canResend}
        >
          {resendsLeft === 0
            ? t('password-reset.resend')
            : resendWait > 0
              ? t('mfa.resend-wait', { seconds: resendWait, left: resendsLeft })
              : t('mfa.resend', { left: resendsLeft })}
        </button>
        <p role="status" className="text-body-2 text-neutral/300 text-center">
          {resendsLeft === 0 ? t('mfa.resend-none') : resendNote}
        </p>
      </div>
    </MfaFrame>
  );
};

export default EmailCodeStep;
